import { useId } from "react";
import styles from "./IconButton.module.css";

/**
 * A square button that shows only an icon, with a tooltip on hover and keyboard focus. The label is the button's accessible
 * name and the tooltip's title; the optional description says what pressing it does and is read as the button's description.
 * `align` keeps the tooltip inside the card for a button at its right edge.
 */
export function IconButton({
  label,
  description,
  icon,
  onClick,
  disabled = false,
  tone = "default",
  align = "center",
}: {
  label: string;
  description?: string;
  icon: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  tone?: "default" | "danger";
  align?: "center" | "end";
}): React.JSX.Element {
  const descriptionId = useId();
  return (
    <span className={styles.wrap}>
      <button
        type="button"
        className={`${styles.button} ${tone === "danger" ? styles.danger : ""}`}
        aria-label={label}
        aria-describedby={description ? descriptionId : undefined}
        disabled={disabled}
        onClick={onClick}
      >
        {icon}
      </button>
      <span className={`${styles.tooltip} ${align === "end" ? styles.alignEnd : ""}`} aria-hidden="true">
        <span className={styles.tooltipTitle}>{label}</span>
        {description && <span className={styles.tooltipText}>{description}</span>}
      </span>
      {description && (
        <span id={descriptionId} className="visually-hidden">
          {description}
        </span>
      )}
    </span>
  );
}
